import React, { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { api } from '../api'
import { FiLock, FiAlertCircle, FiEye, FiEyeOff } from 'react-icons/fi'
import { Modal } from './ui/Modal'

/**
 * Modal shown when user has never changed their password (e.g. new account or admin-set password).
 * Non-dismissible until they set a new password.
 */
export default function ChangePasswordModal({ isOpen }) {
  const { token, user, refreshUser } = useAuth()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (!newPassword || newPassword.length < 8) {
      setError('New password must be at least 8 characters and include uppercase, lowercase and a number.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.')
      return
    }
    if (!token || !user?.id) {
      setError('Session error. Please log in again.')
      return
    }
    setLoading(true)
    try {
      await api.changePassword(token, user.id, newPassword)
      await refreshUser()
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setError(err.detail || err.message || 'Failed to update password.')
    } finally {
      setLoading(false)
    }
  }

  const noop = () => {}

  return (
    <Modal
      open={isOpen}
      onClose={noop}
      variant="legacy"
      size="md"
      closeOnBackdrop={false}
      closeOnEscape={false}
    >
      <div className="max-h-[inherit] overflow-y-auto overscroll-contain p-4 sm:p-6">
        <div className="flex items-center justify-center mb-4">
          <div className="bg-amber-100 dark:bg-amber-900/30 rounded-full p-3">
            <FiLock className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>
        </div>
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white text-center mb-1">
          Set your password
        </h2>
        <p className="text-gray-600 dark:text-dark-300 text-center mb-6 text-sm px-1">
          You haven’t changed your password yet. Please set a new password to secure your account.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-1">Current password</label>
            <div className="relative">
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full min-h-11 pl-3 pr-10 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Enter current password"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(v => !v)}
                className="absolute inset-y-0 right-0 min-w-11 pr-3 flex items-center justify-center text-gray-500 hover:text-gray-700 dark:text-dark-300 dark:hover:text-white focus:outline-none"
                aria-label={showCurrent ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showCurrent ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-1">New password</label>
            <div className="relative">
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full min-h-11 pl-3 pr-10 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="At least 8 characters (uppercase, lowercase, number)"
                autoComplete="new-password"
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowNew(v => !v)}
                className="absolute inset-y-0 right-0 min-w-11 pr-3 flex items-center justify-center text-gray-500 hover:text-gray-700 dark:text-dark-300 dark:hover:text-white focus:outline-none"
                aria-label={showNew ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showNew ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-1">Confirm new password</label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full min-h-11 pl-3 pr-10 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Confirm new password"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(v => !v)}
                className="absolute inset-y-0 right-0 min-w-11 pr-3 flex items-center justify-center text-gray-500 hover:text-gray-700 dark:text-dark-300 dark:hover:text-white focus:outline-none"
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showConfirm ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">
              <FiAlertCircle className="flex-shrink-0" />
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full min-h-11 bg-red-600 text-white py-2.5 px-4 rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-dark-800 font-medium disabled:opacity-50"
          >
            {loading ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </div>
    </Modal>
  )
}
